import { supabase } from '@/integrations/supabase/client';

export interface UploadResult {
  url: string;
  path: string;
  success: boolean;
  error?: string;
}

export class VisitPhotoService {
  private static readonly BUCKET_NAME = 'visit-photos';
  
  /**
   * Upload a photo for a visit
   */
  static async uploadPhoto(
    companyId: string, 
    file: File, 
    visitId?: string
  ): Promise<UploadResult> {
    try {
      // Validate file
      if (!this.isValidImageFile(file)) {
        return {
          url: '',
          path: '',
          success: false,
          error: 'Invalid file type. Only JPG, PNG, and WebP images are allowed.'
        };
      }

      // Check file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        return {
          url: '',
          path: '',
          success: false,
          error: 'File size too large. Maximum size is 5MB.'
        };
      }

      // Generate unique filename
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2);
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `${timestamp}_${randomId}.${fileExtension}`;
      
      // Create path structure: company_id/visits/visit_id_or_temp/filename
      const folderPath = visitId ? `${companyId}/visits/${visitId}` : `${companyId}/visits/temp`;
      const filePath = `${folderPath}/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload error:', error);
        return {
          url: '',
          path: '',
          success: false,
          error: `Upload failed: ${error.message}`
        };
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(this.BUCKET_NAME)
        .getPublicUrl(filePath);

      return {
        url: publicUrl,
        path: filePath,
        success: true
      };

    } catch (error) {
      console.error('Unexpected upload error:', error);
      return {
        url: '',
        path: '',
        success: false,
        error: 'Unexpected error occurred during upload'
      };
    }
  }

  /**
   * Upload multiple photos
   */
  static async uploadMultiplePhotos(
    companyId: string, 
    files: FileList | File[], 
    visitId?: string,
    onProgress?: (uploaded: number, total: number) => void
  ): Promise<UploadResult[]> {
    const results: UploadResult[] = [];
    const fileArray = Array.from(files);

    for (let i = 0; i < fileArray.length; i++) {
      const result = await this.uploadPhoto(companyId, fileArray[i], visitId);
      results.push(result);
      
      if (onProgress) {
        onProgress(i + 1, fileArray.length);
      }
    }

    return results;
  }

  /**
   * Delete a photo from storage
   */
  static async deletePhoto(filePath: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .remove([filePath]);

      if (error) {
        console.error('Delete error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Unexpected delete error:', error);
      return false;
    }
  }

  /**
   * Move temp photos to visit folder after visit is created
   */
  static async moveTempPhotosToVisit(
    companyId: string,
    tempPaths: string[],
    visitId: string
  ): Promise<string[]> {
    const newPaths: string[] = [];

    for (const tempPath of tempPaths) {
      try {
        // Download the temp file
        const { data: fileData, error: downloadError } = await supabase.storage
          .from(this.BUCKET_NAME)
          .download(tempPath);

        if (downloadError || !fileData) {
          console.error('Error downloading temp file:', downloadError);
          continue;
        }

        // Generate new path
        const fileName = tempPath.split('/').pop();
        const newPath = `${companyId}/visits/${visitId}/${fileName}`;

        // Upload to new location
        const { error: uploadError } = await supabase.storage
          .from(this.BUCKET_NAME)
          .upload(newPath, fileData, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Error uploading to new location:', uploadError);
          continue;
        }

        // Delete temp file
        await this.deletePhoto(tempPath);

        newPaths.push(newPath);
      } catch (error) {
        console.error('Error moving temp photo:', error);
      }
    }

    return newPaths;
  }

  /**
   * Get public URL for a stored photo
   */
  static getPhotoUrl(filePath: string): string {
    const { data: { publicUrl } } = supabase.storage
      .from(this.BUCKET_NAME)
      .getPublicUrl(filePath);
    
    return publicUrl;
  }

  /**
   * Validate if file is a valid image
   */
  private static isValidImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    return validTypes.includes(file.type);
  }

  /**
   * Create a thumbnail URL (if using Supabase Image Transformations)
   */
  static getThumbnailUrl(filePath: string, width: number = 200, height: number = 200): string {
    const { data: { publicUrl } } = supabase.storage
      .from(this.BUCKET_NAME)
      .getPublicUrl(filePath, {
        transform: {
          width,
          height,
          resize: 'cover'
        }
      });
    
    return publicUrl;
  }
}

export default VisitPhotoService;
